<template>
  <NavigationBar/>
  <div class="p-6 pt-16 animate-fade-in">
    <!-- Header -->
    <div class="flex items-center justify-between mb-8">
      <div>
        <h2 class="text-3xl font-bold text-gray-800 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
          Orders Tracker
        </h2>
        <p class="text-gray-500 mt-1">Manage and track all your confirmed orders</p>
      </div>

    </div>

    <!-- Main Table -->
    <!-- Main Table -->
    <div
        class="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden hover:shadow-2xl transition-shadow duration-500">
      <div class="overflow-x-auto"
           style="max-height: 520px; overflow-y: auto;scrollbar-width: none; -ms-overflow-style: none; ">
        <table class="min-w-full divide-y divide-gray-200/70">
          <thead class="bg-gradient-to-r from-blue-500 to-purple-600 sticky top-0 z-20">
          <tr>
            <th class="py-3 px-2 text-left text-white font-semibold uppercase tracking-wider text-xs w-10">S.N</th>
            <th class="py-3 px-2 text-left text-white font-semibold uppercase tracking-wider text-xs w-32">Client</th>
            <th class="py-3 px-2 text-left text-white font-semibold uppercase tracking-wider text-xs w-20">Project ID
            </th>
            <th class="py-3 px-2 text-left text-white font-semibold uppercase tracking-wider text-xs w-24">Payment
              Status
            </th>
            <th class="py-3 px-2 text-left text-white font-semibold uppercase tracking-wider text-xs w-20">Kits</th>
            <th class="py-3 px-2 text-left text-white font-semibold uppercase tracking-wider text-xs w-24">Production
              Status
            </th>
            <th class="py-3 px-2 text-left text-white font-semibold items-center justify-center uppercase tracking-wider text-xs w-24">
              Dispatch Lots
            </th>
            <th class="py-3 px-2 text-left text-white font-semibold uppercase tracking-wider text-xs w-28">Dispatch
              Status
            </th>
            <th class="py-3 px-2 text-left text-white font-semibold uppercase tracking-wider text-xs w-24">QC Docs</th>
            <th class="py-3 px-2 text-left text-white font-semibold uppercase tracking-wider text-xs w-28">Dispatch
              Docs
            </th>
            <th class="py-3 px-2 text-left text-white font-semibold uppercase tracking-wider text-xs w-28">Production
              Unit
            </th>
            <th class="py-3 px-2 text-left text-white font-semibold uppercase tracking-wider text-xs w-28">Warranty
            </th>
          </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200/70">
          <tr v-for="(enquiry, index) in enquiries" :key="index"
              class="hover:bg-gray-50/80 transition-all duration-300 group">
            <!-- S.N -->
            <td class="py-0.5 px-2 whitespace-nowrap text-center">
              <span class="bg-blue-100 text-blue-800 text-xs font-medium px-1.5 py-0.5 rounded-full">{{
                  index + 1
                }}</span>
            </td>

            <!-- Client Name -->
            <td class="py-0.5 px-2 font-medium text-gray-900 group-hover:text-blue-600 transition-colors duration-300 cursor-pointer"
                @click="openAddressDialog(enquiry)">
              <div class="min-w-0">
                <div class="text-xs truncate">{{ enquiry.client_name }}</div>
                <div class="text-[11px] text-gray-500 truncate">View details</div>
              </div>
            </td>

            <!-- Project ID -->
            <td class="py-0.5 px-2 whitespace-nowrap">
              <span class="bg-gray-100 text-gray-800 text-xs font-medium px-1.5 py-0.5 rounded">{{
                  enquiry.project_id
                }}</span>
            </td>

            <!-- Payment Status -->
            <td class="py-0.5 px-2 whitespace-nowrap">
            <span class="px-1.5 py-0.5 rounded-full text-xs font-medium flex items-center max-w-[80px]"
                  :class="{
                'bg-green-100 text-green-800': enquiry.payment_received === 'Yes',
                'bg-red-100 text-red-800': enquiry.payment_received === 'No',
                'bg-yellow-100 text-yellow-800': enquiry.payment_received === 'Partial'
              }">
              <span class="w-1.5 h-1.5 rounded-full mr-1"
                    :class="{
                  'bg-green-500': enquiry.payment_received === 'Yes',
                  'bg-red-500': enquiry.payment_received === 'No',
                  'bg-yellow-500': enquiry.payment_received === 'Partial'
                }"></span>
              {{ enquiry.payment_received }}
              <span v-if="enquiry.payment_received === 'Partial'"
                    class="ml-0.5 text-[11px]">({{ enquiry.payment_percentage }}%)</span>
            </span>
            </td>

            <!-- Kits -->
            <td class="py-0.5 px-2 whitespace-nowrap">
              <button @click="openKitsDialog(enquiry)"
                      class="inline-flex items-center px-1.5 py-0.5 border border-transparent text-xs font-medium rounded-full shadow-sm text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all duration-300">
                View
                <svg xmlns="http://www.w3.org/2000/svg" class="h-2.5 w-2.5 ml-0.5" fill="none" viewBox="0 0 24 24"
                     stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                </svg>
              </button>
            </td>

            <!-- Production Status -->
            <td class="py-0.5 px-2 whitespace-nowrap">
              <div class="relative inline-block">
                <select
                    v-model="enquiry.production_status"
                    @change="updateProductionStatus(enquiry.id, enquiry.production_status)"
                    class="appearance-none block pl-2 pr-5 py-0 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-all duration-200 bg-white"
                    :class="{
      'border-yellow-300 focus:ring-yellow-300 bg-yellow-50 text-yellow-800': enquiry.production_status === 'Planned',
      'border-blue-300 focus:ring-blue-300 bg-blue-50 text-blue-800': enquiry.production_status === 'WIP',
      'border-green-300 focus:ring-green-300 bg-green-50 text-green-800': enquiry.production_status === 'Done'
    }"
                >
                  <option v-for="status in ['Planned', 'WIP', 'Done']" :value="status" :key="status">{{
                      status
                    }}
                  </option>
                </select>

                <!-- Dropdown Arrow -->
                <div class="pointer-events-none absolute inset-y-0 left-12 flex items-center px-3 text-gray-500">
                  <svg class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clip-rule="evenodd"/>
                  </svg>
                </div>
              </div>
            </td>

            <!-- Dispatch Date -->
            <td class="py-0.5 px-2 whitespace-nowrap">
              <div class="relative flex justify-center">
                <button @click="openDispatchDialog(enquiry)"
                        class="text-indigo-600 hover:text-indigo-800 font-medium text-xs bg-indigo-50 hover:bg-indigo-100 rounded-lg px-1.5 py-0.5 transition-all duration-200 ease-in-out shadow-sm hover:shadow-md flex items-center gap-0.5">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24"
                       stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                  </svg>
                  Add / View
                </button>
              </div>
            </td>

            <!-- Dispatch Status -->
            <td class="py-0.5 px-2 whitespace-nowrap ">
              <div class="relative inline-block">
                <select
                    v-model="enquiry.dispatch_status"
                    @change="updateDispatchStatus(enquiry.id, enquiry.dispatch_status)"
                    class="appearance-none block pl-2 pr-5 py-0 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-all duration-200 bg-white"
                    :class="{
      'border-yellow-300 focus:ring-yellow-300 bg-yellow-50 text-yellow-800': enquiry.dispatch_status === 'Partial',
      'border-green-300 focus:ring-green-300 bg-green-50 text-green-800': enquiry.dispatch_status === 'Completed'
    }"
                >
                  <option v-for="status in ['Completed', 'Partial']" :value="status" :key="status">{{ status }}</option>
                </select>

                <!-- Dropdown Arrow -->
                <div class="pointer-events-none absolute inset-y-0 left-16 flex items-center px-3 text-gray-500">
                  <svg class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clip-rule="evenodd"/>
                  </svg>
                </div>
              </div>
            </td>

            <!-- QC Docs -->
            <td class="py-0.5 px-2 whitespace-nowrap">
              <button @click="openQCDocsDialog(enquiry)"
                      class="text-blue-600 hover:text-blue-800 font-medium text-xs bg-blue-50 hover:bg-blue-100 rounded-lg px-1.5 py-0.5 transition-all duration-200 ease-in-out shadow-sm hover:shadow-md flex items-center gap-0.5">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24"
                     stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
                View / Upload
              </button>
            </td>

            <!-- Dispatch Docs -->
            <td class="py-0.5 px-2 whitespace-nowrap">
              <button @click="openDispatchDocsDialog(enquiry)"
                      class="text-purple-600 hover:text-purple-800 font-medium text-xs bg-purple-50 hover:bg-purple-100 rounded-lg px-1.5 py-0.5 transition-all duration-200 ease-in-out shadow-sm hover:shadow-md flex items-center gap-0.5">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24"
                     stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
                </svg>
                View / Upload
              </button>
            </td>

            <!-- Production Unit -->
            <td class="py-0.5 px-2 whitespace-nowrap ">
              <div class="relative inline-block">
                <select
                    v-model="enquiry.production_unit"
                    @change="updateProdUnitStatus(enquiry.id, enquiry.production_unit)"
                    class="appearance-none block pl-2 pr-5 py-0 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-all duration-200 bg-white"
                    :class="{
      'border-yellow-300 focus:ring-yellow-300 bg-yellow-50 text-yellow-800': enquiry.production_unit === 'Boisar',
      'border-green-300 focus:ring-green-300 bg-green-50 text-green-800': enquiry.production_unit === 'Ranchi'
    }"
                >
                  <option v-for="status in ['Boisar', 'Ranchi']" :value="status" :key="status">{{ status }}</option>
                </select>

                <!-- Dropdown Arrow -->
                <div class="pointer-events-none absolute inset-y-0 left-11 flex items-center px-3 text-gray-500">
                  <svg class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clip-rule="evenodd"/>
                  </svg>
                </div>
              </div>
            </td>
            <td>
              <router-link :to="{ name: 'ViewOrderWarranties', params: { sr: enquiry.sr } }">
                <!--  this way we send the sr to other component and we also open that component
                   based on sr value because we want to open check warranties based on sr -->
                <button
                    class="bg-green-100 text-green-800 text-xs font-semibold px-3 py-1 rounded-lg hover:bg-green-200 transition-colors duration-200">
                  Check
                </button>
              </router-link>
            </td>

          </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Pagination Controls -->
    <div v-if="totalPages > 1" class="mt-6 flex items-center justify-between bg-white rounded-xl shadow-md p-4">
      <!-- Pagination Info -->
      <div class="flex items-center text-sm text-gray-700">
        <span class="font-medium">Showing {{
            ((currentPage - 1) * pageSize) + 1
          }}-{{ Math.min(currentPage * pageSize, totalCount) }} of {{ totalCount }} orders</span>
      </div>

      <!-- Pagination Buttons -->
      <div class="flex items-center space-x-2">
        <!-- Previous Button -->
        <button
            @click="goToPrevPage"
            :disabled="!hasPrevious"
            :class="{
            'bg-gray-200 text-gray-400 cursor-not-allowed': !hasPrevious,
            'bg-blue-500 text-white hover:bg-blue-600': hasPrevious
          }"
            class="px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24"
               stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
          </svg>
          Previous
        </button>

        <!-- Page Numbers -->
        <div class="flex items-center space-x-1">
          <span v-for="page in Math.min(5, totalPages)" :key="page"
                @click="goToPage(page)"
                :class="{
                  'bg-blue-500 text-white': page === currentPage,
                  'bg-gray-200 text-gray-700 hover:bg-gray-300': page !== currentPage
                }"
                class="px-3 py-2 rounded-lg cursor-pointer font-medium transition-colors duration-200">
            {{ page }}
          </span>
          <span v-if="totalPages > 5" class="px-2 text-gray-500">...</span>
          <span v-if="totalPages > 5 && currentPage < totalPages - 2"
                @click="goToPage(totalPages)"
                class="px-3 py-2 rounded-lg cursor-pointer font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors duration-200">
            {{ totalPages }}
          </span>
        </div>

        <!-- Next Button -->
        <button
            @click="goToNextPage"
            :disabled="!hasNext"
            :class="{
            'bg-gray-200 text-gray-400 cursor-not-allowed': !hasNext,
            'bg-blue-500 text-white hover:bg-blue-600': hasNext
          }"
            class="px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center"
        >
          Next
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24"
               stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Loading Spinner -->
    <div v-if="loading" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 flex items-center space-x-3">
        <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
        <span class="text-gray-700 font-medium">Loading orders...</span>
      </div>
    </div>

    <!-- Address Dialog -->
    <dialog
        ref="dialogRef"
        class="rounded-2xl p-0 w-[90%] max-w-2xl border-0 shadow-xl backdrop:bg-black/30 animate-scale-in overflow-hidden"
    >
      <div class="bg-white">
        <div class="bg-gradient-to-r from-blue-500 to-purple-600 p-6 text-white">
          <div class="flex justify-between items-center">
            <div>
              <h3 class="text-2xl font-bold">{{ selected_address.client_name }}</h3>
              <p class="text-blue-100 mt-1">Order Details</p>
            </div>
            <button
                @click="closeAddressDialog"
                class="text-white/80 hover:text-white transition-colors duration-200"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                   stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        <div class="p-6 space-y-6">
          <div class="p-5 bg-gray-50 rounded-xl border border-gray-200">
            <div class="flex items-center mb-3">
              <div class="bg-blue-100 p-2 rounded-lg mr-3">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24"
                     stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                </svg>
              </div>
              <h4 class="text-lg font-semibold text-gray-800">Delivery Address</h4>
            </div>
            <p class="text-gray-700 whitespace-pre-wrap ml-11">{{ selected_address.delivery_address }}</p>
          </div>

          <div class="p-5 bg-gray-50 rounded-xl border border-gray-200">
            <div class="flex items-center mb-3">
              <div class="bg-purple-100 p-2 rounded-lg mr-3">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-purple-600" fill="none" viewBox="0 0 24 24"
                     stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z"/>
                </svg>
              </div>
              <h4 class="text-lg font-semibold text-gray-800">Billing Address</h4>
            </div>
            <p class="text-gray-700 whitespace-pre-wrap ml-11">{{ selected_address.billing_address }}</p>
          </div>
        </div>

        <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
          <button
              @click="closeAddressDialog"
              class="px-6 py-1 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg hover:from-blue-600 hover:to-purple-600 transition-all duration-300 shadow-md hover:shadow-lg flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24"
                 stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
            Close
          </button>
        </div>
      </div>
    </dialog>

    <!-- Kits Dialog -->
    <dialog
        ref="kitsDialogRef"
        class="rounded-2xl p-0 w-[90%] max-w-4xl border-0 shadow-xl backdrop:bg-black/30 animate-scale-in overflow-hidden"
    >
      <div class="bg-white flex flex-col" style="max-height: 95vh;">
        <!-- Header -->
        <div class="bg-gradient-to-r from-blue-500 to-purple-600 p-6 text-white">
          <div class="flex justify-between items-center">
            <div>
              <h3 class="text-xl font-bold">Kits for {{ selected_kit.client_name }}</h3>
              <p class="text-blue-100 text-sm mt-1">Order Specifications</p>
            </div>
            <button
                @click="closeKitsDialog"
                class="text-white/80 hover:text-white transition-colors duration-200"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                   stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Content -->
        <div class="p-4 overflow-y-auto">
          <div class="overflow-x-auto rounded-lg border border-gray-200 mb-4">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
              <tr>
                <th class="px-4 py-0.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  S.N
                </th>
                <th class="px-4 py-0.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th class="px-4 py-0.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Quantity
                </th>
                <th class="px-4 py-0.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Remaining Quantity
                </th>

                <!-- ✅ Display previous lots -->
                <th v-for="(lot, lotIndex) in kitsLots" :key="'lot' + lotIndex"
                    class="px-4 py-0.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dispatch {{ lotIndex + 1 }}
                </th>

                <!-- ✅ Dynamic Lot Columns -->
                <th v-for="(lot, lotIndex) in lots" :key="'lot-head-' + lotIndex"
                    class="px-4 py-0.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lot
                </th>
                <th class="px-4 py-0.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Create Warranty
                </th>
              </tr>
              </thead>

              <tbody class="bg-white divide-y divide-gray-200">
              <!-- ================= Table Data Rows ================= -->
              <tr v-for="(kit, kitIndex) in selected_kit.kits"
                  :key="kitIndex"
                  class="hover:bg-gray-50 transition-colors duration-200">
                <td class="px-4 py-0.5 whitespace-nowrap">
                  <div class="flex items-center">
                    <div class="flex-shrink-0 h-6 w-6 bg-blue-100 rounded-full flex items-center justify-center">
                      <span class="text-blue-600 text-xs font-medium">{{ kitIndex + 1 }}</span>
                    </div>
                  </div>
                </td>

                <td class="px-4 py-0.5 whitespace-nowrap text-sm font-medium text-gray-900">
                  {{ kit.name }}
                </td>

                <td class="px-4 py-0.5 whitespace-nowrap text-sm text-gray-900">
    <span class="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
      {{ kit.qty }}
    </span>
                </td>

                <!-- ✅ Remaining Quantity -->
                <td class="px-4 py-0.5 whitespace-nowrap text-sm text-gray-900">
    <span class="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded-full">
      {{ maxAllowedInput[kitIndex] }}
    </span>
                </td>

                <!-- ✅ Previous dispatch data -->
                <td v-for="(lot, lotIndex) in kitsLots"
                    :key="'lot' + lotIndex"
                    class="px-4 py-0.5 whitespace-nowrap">
    <span class="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
      {{ lot['kit' + (kitIndex + 1) + '_dispatched'] || 0 }}
    </span>
                </td>

                <!-- ✅ Independent inputs per Lot -->
                <td v-for="(lot, lotIndex) in lots"
                    :key="'lot-input-' + lotIndex"
                    class="px-4 py-0.5 whitespace-nowrap">
                  <input type="number"
                         v-model="lot.kit_dispatch[kitIndex]"
                         :max="maxAllowedInput[kitIndex]"
                         @input="checkMaxValue(lot, kitIndex)"
                         class="border px-2 py-1 rounded w-20 text-sm"/>
                </td>
                <td class="px-4 py-0.5 whitespace-nowrap text-sm text-gray-900">
                  <button
                      @click="goToWarrantyPage(kit)" :disabled="getWarrantyCount(kit.name) <= 0"
                      :class="['px-2 py-0.5 text-xs font-medium rounded-full transition-all duration-200',
                      getWarrantyCount(kit.name) > 0 ? 'bg-purple-600 text-white hover:bg-purple-700 cursor-pointer': 'bg-gray-200 text-gray-500 cursor-not-allowed']"
                  >
                    {{ getWarrantyCount(kit.name) }}
                  </button>
                </td>
              </tr>

              <!-- ================= Total Row ================= -->
              <tr class="hover:bg-gray-50 transition-colors duration-200">
                <td></td>
                <td class="px-4 py-0.5 whitespace-nowrap text-sm font-medium text-gray-900">Total Kits:</td>
                <td class="px-4 py-0.5 whitespace-nowrap text-sm text-gray-900">
    <span class="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
      {{ selected_kit.total_kits }}
    </span>
                </td>
                <td class="px-4 py-0.5 whitespace-nowrap text-sm text-gray-900">
    <span class="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded-full">
      {{ maxAllowedInput.reduce((a, b) => a + b, 0) }}
    </span>
                </td>
                <td v-for="(lot, lotIndex) in kitsLots" :key="'total' + lotIndex" class="px-4 py-0.5 whitespace-nowrap">
    <span class="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
      {{ lot.sum }}
    </span>
                </td>
              </tr>

              <!-- ================= Percentage Row ================= -->
              <tr class="bg-gray-50 hover:bg-gray-100 transition-colors duration-200">
                <td></td>
                <td class="px-4 py-0.5 whitespace-nowrap text-sm font-medium text-gray-900">Percentage:</td>
                <td></td>
                <td></td>
                <td v-for="(lot, lotIndex) in kitsLots" :key="'percent-' + lotIndex"
                    class="px-4 py-0.5 whitespace-nowrap">
                <span class="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                  {{ ((lot.sum / selected_kit.total_kits) * 100).toFixed(2) }}%
                </span>
                </td>
              </tr>

              <!-- ================= Date Row ================= -->
              <tr class="bg-gray-50 hover:bg-gray-100 transition-colors duration-200">
                <td></td>
                <td class="px-4 py-0.5 whitespace-nowrap text-sm font-medium text-gray-900">Date:</td>
                <td></td>
                <td></td>
                <td v-for="(lot, lotIndex) in kitsLots" :key="'date-' + lotIndex"
                    class="px-4 py-0.5 whitespace-nowrap">
                <span class="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                  {{ lot.delivery_date }}
                </span>
                </td>
              </tr>
              </tbody>

            </table>

            <!-- Buttons -->
            <div class="flex gap-2 mt-3">
              <button @click="addLot"
                      class="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                      :disabled="!selected_kit.kits || selected_kit.kits.length === 0">
                + Add Lot
              </button>
              <button @click="saveLots"
                      class="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600">
                Save All Lots
              </button>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="px-4 py-3 bg-gray-50 border-t border-gray-200 flex justify-end">
          <button
              @click="closeKitsDialog"
              class="px-4 py-0.5 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-md
               hover:from-blue-600 hover:to-purple-600 transition-all duration-300 shadow-sm hover:shadow-md flex items-center text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </dialog>

    <!--  Dispatch Date Dialog  -->
    <dialog ref="dispatchDialogRef"
            class="rounded-2xl p-0 w-[90%] max-w-xl border-0 shadow-xl backdrop:bg-black/30 animate-scale-in overflow-hidden">
      <div class="bg-white flex flex-col max-h-[80vh]">
        <!-- Header -->
        <div class="bg-gradient-to-r from-blue-500 to-purple-600 p-6 text-white flex-shrink-0">
          <div class="flex justify-between items-center">
            <div>
              <h3 class="text-xl font-bold">Dispatch Lots</h3>
              <p v-if="selectedDispatchEnquiry" class="text-sm text-blue-100 mt-1">
                for {{ selectedDispatchEnquiry.client_name }} (SR: {{ selectedDispatchEnquiry.sr }})
              </p>
            </div>
            <button @click="closeDispatchDialog" class="text-white hover:text-gray-200 transition">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                   stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Scrollable Content -->
        <div class="p-4 space-y-4 overflow-y-auto flex-grow">
          <!-- Previous Lots -->
          <div v-if="dispatch.length > 0" class="mb-4">
            <div class="flex items-center justify-between mb-2">
              <h4 class="text-md font-semibold text-gray-800 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1 text-blue-600" fill="none"
                     viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                </svg>
                Previous Lots
              </h4>
              <div class="text-sm font-medium text-gray-700">
                Total: <span :class="totalDispatchPercentage > 100 ? 'text-red-600' : 'text-green-600'">{{
                  totalDispatchPercentage
                }}%</span>
              </div>
            </div>

            <div class="overflow-x-auto rounded-lg border border-gray-200 shadow">
              <table class="min-w-full divide-y divide-gray-200 text-sm">
                <thead class="bg-gray-50">
                <tr>
                  <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lot</th>
                  <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">%</th>
                  <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dispatch
                    Date
                  </th>
                  <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Delivery
                    Date
                  </th>
                  <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions
                  </th>
                </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="(lot, index) in activeLots" :key="index" class="hover:bg-blue-50/30">
                  <td class="px-3 py-2 whitespace-nowrap text-center text-gray-900 font-medium">{{ index + 1 }}</td>
                  <td class="px-3 py-2 whitespace-nowrap text-center text-blue-600 font-medium">{{
                      lot.percentage
                    }}%
                  </td>
                  <td class="px-3 py-2 whitespace-nowrap text-center text-gray-500">{{ lot.lot_date_str }}</td>
                  <td class="px-3 py-2 whitespace-nowrap text-center">
                    <div v-if="!lot.delivery_date_str" class="flex items-center justify-center space-x-1">
                      <input
                          type="date"
                          v-model="lot.delivery_date"
                          class="border rounded px-2 py-1 text-xs w-28"
                      >
                      <button
                          @click="saveDeliveryDate(lot)"
                          class="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                      >
                        Save
                      </button>
                    </div>
                    <span v-else class="text-green-600 text-xs">{{ lot.delivery_date_str }}</span>
                  </td>
                  <td class="px-3 py-2 whitespace-nowrap text-center">
                    <button
                        @click="markLotAsDeleted(lot)"
                        class="text-red-500 hover:text-red-700 p-1"
                        title="Delete"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                           stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                      </svg>
                    </button>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>

            <button
                v-if="deletedLots"
                @click="showDeleted = !showDeleted"
                class="mt-2 text-xs text-blue-600 hover:text-blue-800 flex items-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24"
                   stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      :d="showDeleted ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'"/>
              </svg>
              {{ showDeleted ? 'Hide Deleted' : 'Show Deleted' }}
            </button>
          </div>

          <!-- Deleted Lots (Compact) -->
          <div v-if="showDeleted && dispatch.length > 0" class="mb-4">
            <h4 class="text-md font-semibold text-gray-800 mb-2 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1 text-red-600" fill="none" viewBox="0 0 24 24"
                   stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
              </svg>
              Deleted Lots
            </h4>

            <div class="overflow-x-auto rounded-lg border border-gray-200 shadow">
              <table class="min-w-full divide-y divide-gray-200 text-sm">
                <thead class="bg-gray-50">
                <tr>
                  <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lot</th>
                  <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">%</th>
                  <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="(lot, index) in deletedLots" :key="index" class="hover:bg-red-50/30">
                  <td class="px-3 py-2 whitespace-nowrap text-center">{{ index + 1 }}</td>
                  <td class="px-3 py-2 whitespace-nowrap text-center text-red-600">{{ lot.percentage }}%</td>
                  <td class="px-3 py-2 whitespace-nowrap text-center text-gray-500">{{ lot.lot_date_str }}</td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- New Lot Form (Compact) -->
          <div class="bg-blue-50 p-3 rounded-lg">
            <div class="flex items-center justify-between mb-2">
              <label class="text-sm font-medium text-gray-700">
                New Dispatch %
              </label>
              <div class="text-xs font-medium text-gray-700">
                Remaining: <span :class="remDispatchPercentage <= 0 ? 'text-green-600' : 'text-red-600'">{{
                  remDispatchPercentage
                }}%</span>
              </div>
            </div>

            <div class="flex space-x-2">
              <input
                  v-model.number="newLot.percentage"
                  type="number"
                  placeholder="Enter %"
                  class="flex-1 px-3 py-1 border rounded text-sm focus:ring-1 focus:ring-blue-500"
                  min="1"
                  max="100"
              >
              <button
                  @click="saveDispatchData"
                  class="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                  :disabled="!newLot.percentage || newLot.percentage < 1 || newLot.percentage > 100"
                  :class="{'opacity-50 cursor-not-allowed': !newLot.percentage || newLot.percentage < 1 || newLot.percentage > 100}"
              >
                Add
              </button>
            </div>

            <p v-if="!newLot.percentage || newLot.percentage < 1 || newLot.percentage > 100"
               class="mt-1 text-xs text-red-600">
              Enter 1-100%
            </p>
          </div>
        </div>

        <!-- Footer -->
        <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3 flex-shrink-0">
          <button
              @click="closeDispatchDialog"
              class="px-4 py-0.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
          >
            Close
          </button>
          <!--          <button-->
          <!--              @click="saveDispatchData"-->
          <!--              class="px-4 py-0.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 shadow-md hover:shadow-lg transition-all"-->
          <!--          >-->
          <!--            Save Lot-->
          <!--          </button>-->
        </div>
      </div>
    </dialog>

    <!-- QC Docs Dialog -->
    <dialog ref="qcDialogRef"
            class="rounded-2xl p-0 w-[90%] max-w-2xl border-0 shadow-xl backdrop:bg-black/30 animate-scale-in overflow-hidden">
      <div class="bg-white flex flex-col max-h-[80vh]">
        <!-- Header -->
        <div class="bg-gradient-to-r from-blue-500 to-purple-600 p-6 text-white flex-shrink-0">
          <div class="flex justify-between items-center">
            <h3 class="text-xl font-bold">QC Documents</h3>
            <button @click="closeQCDocsDialog" class="text-white hover:text-gray-200 transition">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                   stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Scrollable Content -->
        <div class="p-6 space-y-6 overflow-y-auto flex-grow">
          <!-- File List -->
          <ul v-if="qcFiles.length" class="space-y-3">
            <li v-for="file in qcFiles.filter(f => f.deleted !== 'Yes')" :key="file.id"
                class="flex items-center justify-between p-3 rounded-lg bg-gray-50 border border-gray-200 hover:bg-gray-100 transition">
              <div class="font-medium text-gray-800 truncate max-w-[120px] sm:max-w-xs" :title="file.file_name">
                {{ file.file_name }}
              </div>
              <a :href="file.file" target="_blank"
                 class="text-blue-600 font-medium truncate">{{ file.file.split('/').pop() }}</a>
              <span class="text-xs text-gray-500 ml-3">{{ file.uploaded_at_str }}</span>
              <button
                  @click="deleteQCFile(file)"
                  class="ml-3 text-red-600 hover:text-red-800 text-xs font-bold rounded px-2 py-1"
                  title="Delete file"
              >
                Delete
              </button>
            </li>
          </ul>
          <button
              @click="showDeletedQCFiles = !showDeletedQCFiles"
              class="text-xs text-blue-600 hover:text-blue-800 flex items-center mb-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24"
                 stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    :d="showDeletedQCFiles ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'"/>
            </svg>
            {{ showDeletedQCFiles ? 'Hide Deleted QC Files' : 'Show Deleted QC Files' }}
          </button>
          <ul v-if="showDeletedQCFiles && qcFiles.some(f => f.deleted === 'Yes')" class="space-y-3 mt-2">
            <li v-for="file in qcFiles.filter(f => f.deleted === 'Yes')" :key="file.id"
                class="flex items-center justify-between p-3 rounded-lg bg-red-50 border border-red-200 text-xs sm:text-sm">
              <div class="font-medium text-gray-800 truncate max-w-[140px] sm:max-w-xs" :title="file.file_name">
                {{ file.file_name }}
              </div>
              <div class="italic text-gray-500 flex-1">[Deleted]</div>
              <span class="text-xs text-gray-500 ml-3">{{ file.uploaded_at_str }}</span>
            </li>
          </ul>

          <p v-else class="text-gray-500 italic">No files uploaded yet.</p>

          <!-- File Upload -->
          <div class="flex gap-4">
            <!-- Document Name Input -->
            <div class="flex-1">
              <label for="QcdocumentName" class="block text-sm font-medium text-gray-700 mb-1">Name of Document</label>
              <input
                  id="QcdocumentName"
                  v-model="QcdocumentName"
                  type="text"
                  placeholder="e.g., Invoice"
                  class="w-full px-3 py-0.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <!-- File Upload -->
            <div class="flex-1">
              <label class="block text-sm font-medium text-gray-700 mb-1">Upload QC Documents</label>
              <input type="file" multiple @change="handleFiles"
                     class="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:outline-none hover:border-blue-500 transition"/>
            </div>
          </div>

        </div>

        <!-- Footer -->
        <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3 flex-shrink-0">
          <button @click="closeQCDocsDialog"
                  class="px-4 py-0.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition">Close
          </button>
          <button @click="uploadQCFiles"
                  class="px-4 py-0.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:from-green-600 hover:to-emerald-700 shadow-md hover:shadow-lg transition-all">
            Upload
          </button>
        </div>
      </div>
    </dialog>

    <!-- Dispatch Docs Dialog -->
    <dialog ref="dispatchDocsDialogRef"
            class="rounded-2xl p-0 w-[90%] max-w-2xl border-0 shadow-xl backdrop:bg-black/30 animate-scale-in overflow-hidden">
      <div class="bg-white flex flex-col max-h-[80vh]">
        <!-- Header -->
        <div class="bg-gradient-to-r from-blue-500 to-purple-600 p-6 text-white flex-shrink-0">
          <div class="flex justify-between items-center">
            <h3 class="text-xl font-bold">Dispatch Documents</h3>
            <p class="text-sm text-blue-100">for SR: {{ selectedDispatchSR }}</p>
            <button @click="closeDispatchDocsDialog" class="text-white hover:text-gray-200 transition">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                   stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Scrollable Content -->
        <div class="p-6 space-y-6 overflow-y-auto flex-grow">
          <!-- File List -->
          <ul class="space-y-2 text-sm text-gray-700 mb-4">
            <li v-for="file in dispatchFiles_show.filter(f => f.deleted !== 'Yes')" :key="file.id"
                class="flex items-center gap-3 p-2 rounded-lg bg-gray-50 border border-gray-200 text-xs sm:text-sm">
              <!-- Document Name -->
              <div class="font-medium text-gray-800 truncate max-w-[120px] sm:max-w-xs" :title="file.file_name">
                {{ file.file_name }}
              </div>

              <!-- File Icon + Link -->
              <a :href="file.file" target="_blank"
                 class="text-blue-600 hover:underline truncate flex-1 text-center">
                {{ file.file.split('/').pop() }}
              </a>

              <!-- Upload Date -->
              <div class="text-gray-500 whitespace-nowrap text-xs sm:text-sm">
                {{ file.uploaded_at_str }}
              </div>

              <!-- Delete Button -->
              <button
                  @click="deleteDispatchFile(file)"
                  class="ml-2 text-red-600 hover:text-red-800 text-xs font-bold rounded px-2 py-1"
                  title="Delete file"
              >Delete
              </button>
            </li>
          </ul>
          <button
              @click="showDeletedDispatchFiles = !showDeletedDispatchFiles"
              class="text-xs text-blue-600 hover:text-blue-800 flex items-center mb-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24"
                 stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    :d="showDeletedDispatchFiles ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'"/>
            </svg>
            {{ showDeletedDispatchFiles ? 'Hide Deleted Files' : 'Show Deleted Files' }}
          </button>
          <ul v-if="showDeletedDispatchFiles && dispatchFiles_show.some(f => f.deleted === 'Yes')"
              class="space-y-2 text-sm text-gray-700 mb-2">
            <li v-for="file in dispatchFiles_show.filter(f => f.deleted === 'Yes')" :key="file.id"
                class="flex items-center gap-3 p-2 rounded-lg bg-red-50 border border-red-200 text-xs sm:text-sm">
              <!-- Document Name -->
              <div class="font-medium text-gray-800 truncate max-w-[120px] sm:max-w-xs" :title="file.file_name">
                {{ file.file_name }}
              </div>
              <!-- Since file is deleted, do NOT show download link -->
              <div class="italic text-gray-500 flex-1">[Deleted]</div>
              <!-- Upload Date -->
              <div class="text-gray-500 whitespace-nowrap text-xs sm:text-sm">
                {{ file.uploaded_at_str }}
              </div>
            </li>
          </ul>


          <!-- Side by Side Input + File Upload -->
          <div class="flex flex-col sm:flex-row gap-4 mb-4">
            <!-- Name of Document -->
            <div class="flex-1">
              <label for="documentName" class="block text-sm font-medium text-gray-700 mb-1">Name of Document</label>
              <input
                  id="documentName"
                  v-model="documentName"
                  type="text"
                  placeholder="e.g., Invoice"
                  class="w-full px-3 py-0.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <!-- File Upload -->
            <div class="flex-1">
              <label class="block text-sm font-medium text-gray-700 mb-1">Upload File</label>
              <input
                  type="file"
                  multiple
                  @change="handleDispatchFiles"
                  class="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:outline-none hover:border-blue-500 transition"
              />
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3 flex-shrink-0">
          <button @click="closeDispatchDocsDialog"
                  class="px-4 py-0.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition">Close
          </button>
          <button @click="uploadDispatchFiles"
                  class="px-4 py-0.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:from-green-600 hover:to-emerald-700 shadow-md hover:shadow-lg transition-all">
            Upload
          </button>
        </div>
      </div>
    </dialog>


  </div>
</template>

<script>
import axios from '../axios-setup';
import NavigationBar from "@/components/NavigationBar.vue";

export default {
  name: 'EnquiryTable',
  data() {
    return {
      enquiries: [],
      // Pagination state
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      pageSize: 10,
      hasNext: false,
      hasPrevious: false,
      loading: false,
      selected_address: {
        client_name: '',
        delivery_address: '',
        billing_address: '',
      },
      selected_kit: {
        client_name: '',
        total_kits: '',
        kits: []
      },
      selectedDispatchEnquiry: '',
      dispatch: [],
      newLot: {
        percentage: null,
      },
      lot_d: [],
      selectedQCSR: null,
      qcFiles: [],
      newFiles: [],
      selectedDispatchSR: null,
      dispatchFiles_show: [],
      dispatchFiles_save: [],
      QcdocumentName: '',
      documentName: '',
      showDeleted: false,
      showDeletedDispatchFiles: false,
      showDeletedQCFiles: false,
      lots: [],
      kitsLots: [],
      maxAllowedInput: [],
      selectedKitsWithWarranty: [],
      WCdata: [], // Warranty creation Data
      WCdata2: [], // Warranty creation Data
    }
  },
  components: {
    NavigationBar
  },
  computed: {
    totalDispatchPercentage() {
      return this.dispatch
          .filter(lot => lot.deleted === 'No') // Only include non-deleted lots
          .reduce((sum, lot) => sum + Number(lot.percentage), 0);
    },
    remDispatchPercentage() {
      let tot = this.dispatch.filter(lot => lot.deleted === 'No').reduce((sum, lot) => sum + Number(lot.percentage), 0);
      return 100 - tot
    },
    activeLots() {
      return this.dispatch.filter(lot => lot.deleted === 'No');
    },
    deletedLots() {
      return this.dispatch.filter(lot => lot.deleted === 'Yes');
    },
  },
  methods: {
    async fetchEnquiries(page = 1) {
      try {
        this.loading = true
        const params = {
          page: page,
          page_size: this.pageSize,
          ordering: '-created_at'
        }

        const queryString = Object.keys(params)
            .map(key => `${key}=${params[key]}`)
            .join('&')

        const res = await axios.get(`enquiry/?${queryString}`)

        // Handle paginated response
        if (res.data && res.data.results && Array.isArray(res.data.results)) {
          this.enquiries = res.data.results
          this.totalCount = res.data.count || 0
          this.totalPages = Math.ceil(this.totalCount / this.pageSize)
          this.hasNext = !!res.data.next
          this.hasPrevious = !!res.data.previous
          this.currentPage = page
        } else if (Array.isArray(res.data)) {
          // Fallback for non-paginated response
          this.enquiries = res.data
          this.totalCount = res.data.length
          this.totalPages = 1
          this.hasNext = false
          this.hasPrevious = false
          this.currentPage = 1
        } else {
          console.warn('Unexpected API response format:', res.data)
          this.enquiries = []
          this.totalCount = 0
          this.totalPages = 1
          this.hasNext = false
          this.hasPrevious = false
        }
      } catch (error) {
        console.error("Error fetching enquiries:", error)
        this.enquiries = []
        this.totalCount = 0
        this.totalPages = 1
        this.hasNext = false
        this.hasPrevious = false
      } finally {
        this.loading = false
      }
    },
    getKitSummary(enq) {
      let kits = []
      for (let i = 1; i <= 8; i++) {
        const name = enq[`kit${i}_name`]
        const qty = enq[`kit${i}_qty`]
        if (name && qty && qty > 0) {
          kits.push(`${name}: ${qty}`)
        }
      }
      return kits.join(', ')
    },
    openAddressDialog(enquiry) {
      this.selected_address = {
        client_name: enquiry.client_name,
        delivery_address: enquiry.delivery_address,
        billing_address: enquiry.billing_address,
      }
      this.$refs.dialogRef.showModal()
    },
    closeAddressDialog() {
      this.$refs.dialogRef.close()
    },
    async openKitsDialog(enquiry) {
      const kits = []
      for (let i = 1; i <= 8; i++) {
        const name = enquiry[`kit${i}_name`]
        const qty = enquiry[`kit${i}_qty`]
        if (name && qty && qty > 0) {
          kits.push({name, qty})
        }
      }
      console.log('sr = ', enquiry.sr)
      this.selected_kit = {
        sr: enquiry.sr,
        client_name: enquiry.client_name,
        total_kits: enquiry.total_kits,
        kits
      }
      axios.get(`kitdispatch/?sr=${enquiry.sr}`)
          .then(res => {
            this.kitsLots = res.data || [];

            if (this.kitsLots.length > 0) {
              const kitKeys = Object.keys(this.kitsLots[0]).filter(k => k.startsWith('kit'));
              this.kitsLotsAddition = kitKeys.map(key =>
                  this.kitsLots.reduce((sum, item) => sum + (Number(item[key]) || 0), 0)
              );
            } else {
              // If no dispatches exist, initialize with 0
              this.kitsLotsAddition = this.selected_kit.kits.map(() => 0);
            }

            // ✅ Calculate max allowed input safely
            this.maxAllowedInput = this.selected_kit.kits.map((kit, index) => {
              const dispatched = this.kitsLotsAddition[index] || 0;
              const qty = Number(kit.qty) || 0;
              return Math.max(qty - dispatched, 0);
            });

            console.log('Max Allowed:', this.maxAllowedInput);
            console.log('kits Lots:', this.kitsLots);
          });
      try {
        // 1️⃣ Get all kits first
        const response = await axios.get(`oitems/?sr=${enquiry.sr}`);
        const kits = response.data; // assume array of { kit_name, warranty_approved_count }

        // 2️⃣ For each kit, fetch its created warranty count
        const WCdataPromises = kits.map(async (item) => {
          const response2 = await axios.get(`created-warranties/?sr=${enquiry.sr}&kit_name=${item.kit_name}`);
          const createdCount = response2.data.length;

          return {
            kit_name: item.kit_name,
            warranty_approved_count: item.warranty_approved_count - createdCount,
          };
        });

        // 3️⃣ Wait for all async operations to complete
        this.WCdata = await Promise.all(WCdataPromises);

        console.log("WCdata:", this.WCdata);
      } catch (error) {
        console.error("Error fetching WC data:", error);
      }

      this.$refs.kitsDialogRef.showModal()
    },
    async onCreateWarranty(sr, kitName) {
      try {
        await this.$axios.post('/api/warranty/create-batch/', {
          sr,
          kit_name: kitName,
          count: 1,
        });
        this.$toast?.success?.('Warranty created');
      } catch (err) {
        alert(err?.response?.data?.detail || 'Failed to create warranty');
      }
    },
    closeKitsDialog() {
      this.$refs.kitsDialogRef.close()
    },
    async updateProductionStatus(sr, newStatus) {
      try {
        const response = await axios.patch(`enquiry/${sr}/`, {
          production_status: newStatus
        });
        console.log("Status updated successfully", response.data);
      } catch (error) {
        console.error("Error updating production status:", error);
        // alert("Failed to update production status");
      }
    },
    async updateDispatchStatus(sr, newStatus) {
      try {
        const response = await axios.patch(`enquiry/${sr}/`, {
          dispatch_status: newStatus
        });
        console.log("Status updated successfully", response.data);
      } catch (error) {
        console.error("Error updating production status:", error);
        // alert("Failed to update production status");
      }
    },
    async updateProdUnitStatus(sr, newStatus) {
      try {
        const response = await axios.patch(`enquiry/${sr}/`, {
          production_unit: newStatus
        });
        console.log("Status updated successfully", response.data);
      } catch (error) {
        console.error("Error updating production status:", error);
        // alert("Failed to update production status");
      }
    },
    async updateDeliveryStatus(sr, newStatus) {
      try {
        const response = await axios.patch(`enquiry/${sr}/`, {
          delivery_status: newStatus
        });
        console.log("Status updated successfully", response.data);
      } catch (error) {
        console.error("Error updating production status:", error);
        // alert("Failed to update production status");
      }
    },
    openDispatchDialog(enquiry) {
      this.selectedDispatchEnquiry = enquiry;
      this.dispatch = [];

      axios.get(`dispatch/?sr=${enquiry.sr}`).then(res => {
        if (res.data.length > 0) {
          this.dispatch = res.data;
        }
        this.$refs.dispatchDialogRef.showModal();
      }).catch(err => {
        console.error("Error fetching dispatch lots", err);
        this.dispatch = [];
        this.$refs.dispatchDialogRef.showModal();
      });
    },

    closeDispatchDialog() {
      this.$refs.dispatchDialogRef.close();
    },

    formattedDate() {
      // Get current date and time
      const now = new Date();
      return now.toLocaleString('en-GB', { // 'en-GB' to get DD/MM/YYYY format
        hour: '2-digit',
        minute: '2-digit',
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      }).replace(',', '');  // Remove comma between date and tim

    },

    async saveDispatchData() {
      const percentage = this.newLot.percentage;

      // Basic validation
      if (!percentage || percentage < 1 || percentage > 100) {
        alert('Please enter a valid percentage between 1 and 100.');
        return;
      }

      // Calculate total percentage so far
      const totalSoFar = this.dispatch.filter(lot => lot.deleted === 'No').reduce((sum, lot) => sum + Number(lot.percentage), 0);
      const newTotal = totalSoFar + Number(percentage);

      // Check if it exceeds 100%
      if (newTotal > 100) {
        alert(`Total dispatched percentage cannot exceed 100%. Current total is ${totalSoFar}%.`);
        return;
      }

      try {
        const lot_number = this.dispatch.length + 1;
        let current_sr = this.selectedDispatchEnquiry?.sr || (this.dispatch[0]?.sr);

        const res = await axios.post('dispatch/', {
          sr: current_sr,
          lot_number: lot_number,
          percentage: percentage,
          deleted: 'No',
          lot_date_str: this.formattedDate(),
        });

        this.dispatch.push(res.data);          // Update local list
        this.newLot.percentage = null;         // Reset input field
        await this.refreshEnquiryStatus(current_sr); // <--- ADD HERE!!!
      } catch (error) {
        console.error("Error saving dispatch data", error);
        alert("Failed to save dispatch data.");
      }
    },
    async markLotAsDeleted(lot) {
      try {
        await axios.patch(`dispatch/${lot.id}/`, {deleted: 'Yes'});

        // Update locally:
        lot.deleted = true; // Mark as deleted
        await this.refreshEnquiryStatus(this.selectedDispatchEnquiry.sr); // <--- ADD HERE!!!

        // If your computed properties look like:
        // activeLots: () => this.dispatch.filter(lot => !lot.deleted)
        // deletedLots: () => this.dispatch.filter(lot => lot.deleted)
        // ...Vue will update the display automatically!

        // Optional: show a passive success message in dialog
        this.feedbackMessage = "Lot moved to deleted.";
        setTimeout(() => (this.feedbackMessage = ""), 1500);
      } catch (error) {
        console.error("Failed to delete lot", error);
        this.feedbackMessage = "Failed to delete lot.";
        setTimeout(() => (this.feedbackMessage = ""), 2000);
      }
    },
    openQCDocsDialog(enquiry) {
      this.selectedQCSR = enquiry.sr;
      this.qcFiles = [];
      this.newFiles = [];

      axios.get(`qcdocs/?sr=${enquiry.sr}`)
          .then(res => {
            this.qcFiles = res.data;
            this.$refs.qcDialogRef.showModal();
          });
    },
    closeQCDocsDialog() {
      this.$refs.qcDialogRef.close();
    },
    handleFiles(event) {
      this.newFiles = Array.from(event.target.files);
      /* it takes all the selected file from <input> and convert it into array and
      than add in newFiles Array */
    },
    async uploadQCFiles() {
      for (let file of this.newFiles) {
        const singleForm = new FormData();
        /* FormData is a built-in JavaScript object used to construct key/value
        pairs to send via multipart/form-data, which is needed for file uploads.*/
        /* In simple words FormData() is use to add files in db and singleForm is its object */
        singleForm.append('file', file);
        singleForm.append('file_name', this.QcdocumentName);
        singleForm.append('sr', this.selectedQCSR);
        singleForm.append('uploaded_at_str', this.formattedDate());
        /* here we ad all the data into singleForm that needs to be added in db with
        their column name  */
        await axios.post('qcdocs/', singleForm, {
          headers: {'Content-Type': 'multipart/form-data'}
        });
        /* singlrForm contains file and the data so we ssend that */
        /* headers: {'Content-Type': 'multipart/form-data'} tells Axios to send this request as a file upload. */
      }

      this.openQCDocsDialog({sr: this.selectedQCSR});  // Refresh
    },
    openDispatchDocsDialog(enquiry) {
      this.selectedDispatchSR = enquiry.sr;
      this.dispatchFiles_show = [];
      this.dispatchFiles_save = [];

      axios.get(`dispatchdocs/?sr=${enquiry.sr}`)
          .then(res => {
            this.dispatchFiles_show = res.data;
            this.$refs.dispatchDocsDialogRef.showModal();
          });
    },
    closeDispatchDocsDialog() {
      this.$refs.dispatchDocsDialogRef.close();
    },
    handleDispatchFiles(event) {
      this.dispatchFiles_save = Array.from(event.target.files);
      /* it takes all the selected file from <input> and convert it into array and
      than add in newFiles Array */
    },
    async uploadDispatchFiles() {
      for (let file of this.dispatchFiles_save) {
        const singleForm = new FormData();

        singleForm.append('file', file);
        singleForm.append('file_name', this.documentName);
        singleForm.append('sr', this.selectedDispatchSR);
        singleForm.append('uploaded_at_str', this.formattedDate());

        await axios.post('dispatchdocs/', singleForm, {
          headers: {'Content-Type': 'multipart/form-data'}
        });
      }

      this.openDispatchDocsDialog({sr: this.selectedDispatchSR});  // Refresh
    },
    async saveDeliveryDate(lot) {
      try {
        if (!lot.delivery_date) {
          // show a warning inside the dialog, not alert
          this.feedbackMessage = "Please select a delivery date.";
          return;
        }

        await axios.patch(`dispatch/${lot.id}/`, {
          delivery_date_str: lot.delivery_date,
        });
        // If the API call succeeds, update the local lot object to show the date immediately:
        lot.delivery_date_str = lot.delivery_date;

        // Optional: Clear the editing input if you want
        // lot.delivery_date = "";

        // Optional: Set a success message
        this.feedbackMessage = "Delivery date saved!";
        setTimeout(() => (this.feedbackMessage = ""), 1800);

        // No need to reload full dialog, Vue will reactively update the table row!
      } catch (error) {
        console.error("Error saving delivery date:", error);
        this.feedbackMessage = "Failed to save delivery date.";
        setTimeout(() => (this.feedbackMessage = ""), 2000);
      }
    },
    async deleteQCFile(file) {
      if (!confirm("Are you sure you want to delete this file?")) return;
      try {
        await axios.patch(`qcdocs/${file.id}/`, {
          deleted: "Yes"
        });
        // Re-fetch QC docs to update the list
        this.openQCDocsDialog({sr: this.selectedQCSR});
      } catch (err) {
        console.error("Error deleting QC document", err);
        alert("Failed to delete file.");
      }
    },
    async deleteDispatchFile(file) {
      if (!confirm("Are you sure you want to delete this file?")) return;
      try {
        await axios.patch(`dispatchdocs/${file.id}/`, {deleted: "Yes"});
        // Refresh the list so UI updates
        this.openDispatchDocsDialog({sr: this.selectedDispatchSR});
      } catch (error) {
        alert("Failed to delete the file. Please try again.");
      }
    },
    async refreshEnquiryStatus(sr) {
      try {
        // Get the latest Enquiry data for this sr
        const res = await axios.get(`/enquiry/?sr=${sr}`);
        // If your API returns an array (filtering by sr), take the first one
        const updatedEnquiry = Array.isArray(res.data) ? res.data[0] : res.data;
        // Find the enquiry in your local enquiries array and update it
        const idx = this.enquiries.findIndex(e => e.sr == sr);
        if (idx !== -1 && updatedEnquiry) {
          this.enquiries[idx] = {...this.enquiries[idx], ...updatedEnquiry};
        }
      } catch (error) {
        console.error("Failed to refresh enquiry after dispatch update", error);
      }
    },
    async fetchKitsLots() {
      try {
        const response = await axios.get(`kitdispatch/?sr=${this.selected_kit.sr}`);
        this.kitsLots = response.data;

        // ✅ Update kitsLotsAddition
        if (this.kitsLots.length > 0) {
          const kitKeys = Object.keys(this.kitsLots[0]).filter(k => k.startsWith('kit'));
          this.kitsLotsAddition = kitKeys.map(key =>
              this.kitsLots.reduce((sum, item) => sum + (Number(item[key]) || 0), 0)
          );
        }
      } catch (error) {
        console.error("Error fetching updated lots:", error);
      }
    },
    addLot() {
      const lotNumber = this.lots.length + 1;

      // ✅ Create independent array for each lot
      const dispatchArray = this.selected_kit.kits.map(() => 0)

      this.lots.push({
        lot_name: `Lot ${lotNumber}`,
        kit_dispatch: dispatchArray,
      });
    },
    updateMaxAllowedInput() {
      if (!this.selected_kit || !this.selected_kit.kits) return;

      // Step 1: Calculate total dispatched for each kit from kitsLots
      if (this.kitsLots.length > 0) {
        const kitKeys = Object.keys(this.kitsLots[0]).filter(k => k.startsWith('kit'));
        this.kitsLotsAddition = kitKeys.map(key =>
            this.kitsLots.reduce((sum, item) => sum + (Number(item[key]) || 0), 0)
        );
      } else {
        this.kitsLotsAddition = this.selected_kit.kits.map(() => 0);
      }

      // Step 2: Calculate remaining quantity per kit
      this.maxAllowedInput = this.selected_kit.kits.map((kit, index) => {
        const dispatched = this.kitsLotsAddition[index] || 0;
        const qty = Number(kit.qty) || 0;
        return Math.max(qty - dispatched, 0);
      });
    },
    getWarrantyCount(kitName) {
      const kit = this.WCdata.find(item => item.kit_name === kitName);
      return kit ? kit.warranty_approved_count : '-';
    },
    goToWarrantyPage(kit) {
      this.$router.push({
        name: 'WarrantyGenerator', // your route name
        query: {
          sr: this.selected_kit.sr,
          kit_name: kit.name,
          client_name: this.selected_kit.client_name,
        }
      });
    },
    async saveLots() {
      if (!this.selected_kit || !this.selected_kit.sr) {
        alert("No enquiry selected to save lots.");
        return;
      }

      if (this.lots.length === 0) {
        alert("Please add a lot before saving.");
        return;
      }

      // Get the latest (last added) lot only
      const latestLot = this.lots[this.lots.length - 1];
      // ✅ Calculate sum dynamically inside the function
      const totalSum = latestLot.kit_dispatch.reduce((total, value) => total + (Number(value) || 0), 0);


      try {
        await axios.post("kitdispatch/", {
          sr: this.selected_kit.sr,
          kit1_dispatched: latestLot.kit_dispatch[0] || 0,
          kit2_dispatched: latestLot.kit_dispatch[1] || 0,
          kit3_dispatched: latestLot.kit_dispatch[2] || 0,
          kit4_dispatched: latestLot.kit_dispatch[3] || 0,
          kit5_dispatched: latestLot.kit_dispatch[4] || 0,
          kit6_dispatched: latestLot.kit_dispatch[5] || 0,
          kit7_dispatched: latestLot.kit_dispatch[6] || 0,
          kit8_dispatched: latestLot.kit_dispatch[7] || 0,
          sum: totalSum,
          delivery_date: this.formattedDate(),
        });
        alert("Lot saved successfully!");

        // Reset the lot input after save
        this.lots = [];
        // ✅ Fetch updated lots immediately
        await this.fetchKitsLots();
        this.updateMaxAllowedInput();
      } catch (error) {
        console.error("Error saving lot:", error);
        alert("Error while saving lot data");
      }
    },
    checkMaxValue(lot, kitIndex) {
      const max = this.maxAllowedInput[kitIndex];
      if (lot.kit_dispatch[kitIndex] > max) {
        lot.kit_dispatch[kitIndex] = max;  // force limit
      }
      if (lot.kit_dispatch[kitIndex] < 0) {
        lot.kit_dispatch[kitIndex] = 0;  // prevent negative values
      }
    },
    // Pagination methods
    goToNextPage() {
      if (this.hasNext && this.currentPage < this.totalPages) {
        this.fetchEnquiries(this.currentPage + 1)
      }
    },
    goToPrevPage() {
      if (this.hasPrevious && this.currentPage > 1) {
        this.fetchEnquiries(this.currentPage - 1)
      }
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
        this.fetchEnquiries(page)
      }
    }
  },
  watch: {
    kitsLots: {
      handler() {
        this.updateMaxAllowedInput();
      },
      deep: true,
    },
    'selected_kit.kits': {
      handler() {
        this.updateMaxAllowedInput();
      },
      deep: true,
    }
  },
  mounted() {
    this.fetchEnquiries()
  }
}
</script>
<style scoped>
/* Animation Styles */
.animate-scale-in {
  animation: scaleIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.production-status-dropdown {
  width: 120px; /* Fixed width */
  min-width: 120px; /* Ensure it doesn't shrink */
}

/* Dialog Scroll Behavior */
.dialog-content {
  max-height: 60vh;
  overflow-y: auto;
}

/* Custom Scrollbar */
.dialog-content::-webkit-scrollbar {
  width: 6px;
}

.dialog-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.dialog-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.dialog-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Table Compact Styling */
.compact-table th, .compact-table td {
  padding: 0.5rem 1rem; /* Smaller padding */
  font-size: 0.875rem; /* Smaller font size */
}

.animate-scale-in {
  animation: scaleIn 0.4s ease-out forwards;
}

@keyframes scaleIn {
  from {
    transform: scale(0.95);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

/* Hide scroll bar for this table wrapper */
::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

</style>
